import React, { useMemo } from 'react';
import { useCanvasStore } from '../../store/canvas-store';
import type { StyleguideFrame, ThemeToken, ThemeModeTokens } from '../../store/types';
import { effectiveModeTokens } from '../../store/types';
import { useTheme, effectiveTheme } from '../theme';
import { resolveModeRefs } from '../../style/theme-compiler';

interface Props { frame: StyleguideFrame }

/** Visualizes a theme's tokens. Theme = frame.themeId, else the canvas
 *  activeThemeId. Pure render from the store → live-updates on theme edits. */
export function StyleguideFrameView({ frame }: Props) {
  const themes = useCanvasStore((s) => s.doc.themes) ?? [];
  const activeThemeId = useCanvasStore((s) => s.doc.activeThemeId);
  const dispatch = useCanvasStore((s) => s.dispatch);
  const targetId = frame.themeId ?? activeThemeId;
  const theme = useMemo(
    () => themes.find((t) => t.id === targetId),
    [themes, targetId],
  );
  const appTheme = useTheme((s) => s.theme);
  const isDark = effectiveTheme(appTheme) === 'dark';

  return (
    <div
      className="p-3 text-xs overflow-auto h-full"
      onMouseDown={(e) => e.stopPropagation()}
      style={{ background: 'var(--color-surface)', color: 'var(--color-text)' }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold">{theme ? theme.name : 'No theme'}</span>
        <span className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>{isDark ? 'dark' : 'light'}</span>
        <select
          value={frame.themeId ?? ''}
          onChange={(e) => dispatch({ t: 'frame.setTheme', id: frame.id, themeId: e.target.value || undefined })}
          className="cf-select rounded px-1 py-0.5 text-[11px]"
          style={{ background: 'var(--color-input-bg)', border: '1px solid var(--color-input-border)', color: 'var(--color-text)' }}
        >
          <option value="">— active —</option>
          {themes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      {!theme ? (
        <div className="italic" style={{ color: 'var(--color-text-muted)' }}>
          Pick a theme (or set a canvas active theme) to visualize it.
        </div>
      ) : (
        <StyleguideBody theme={theme} isDark={isDark} />
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--color-text-muted)' }}>{title}</div>
      {children}
    </div>
  );
}

function StyleguideBody({ theme, isDark }: { theme: ThemeToken; isDark: boolean }) {
  const mode: ThemeModeTokens = effectiveModeTokens(theme, isDark);
  const t = resolveModeRefs(mode);
  const colors = Object.entries(t.colors);
  const types = Object.entries(t.typography);
  const radii = Object.entries(t.rounded);
  const spacing = Object.entries(t.spacing);
  const components = Object.entries(t.components ?? {});

  return (
    <div>
      {colors.length > 0 && (
        <Section title="colors">
          <div className="flex flex-wrap gap-2">
            {colors.map(([k, v]) => (
              <div key={k} className="text-center" title={`${k}: ${v}`}>
                <div style={{ width: 36, height: 36, borderRadius: 6, background: v, border: '1px solid var(--color-border)' }} />
                <div className="text-[9px] mt-0.5">{k}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
      {types.length > 0 && (
        <Section title="type">
          {types.map(([k, ty]) => (
            <div key={k} style={{ fontFamily: ty.fontFamily, fontSize: ty.fontSize, fontWeight: ty.fontWeight as number | undefined, lineHeight: ty.lineHeight as number | undefined }}>
              {k} — Ag
            </div>
          ))}
        </Section>
      )}
      {radii.length > 0 && (
        <Section title="radii">
          <div className="flex flex-wrap gap-2">
            {radii.map(([k, v]) => (
              <div key={k} className="text-center" title={`${k}: ${v}`}>
                <div style={{ width: 36, height: 24, borderRadius: v, background: 'var(--color-border)' }} />
                <div className="text-[9px] mt-0.5">{k}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
      {spacing.length > 0 && (
        <Section title="spacing">
          {spacing.map(([k, v]) => (
            <div key={k} className="flex items-center gap-2">
              <div style={{ height: 8, width: typeof v === 'number' ? `${v}px` : v, background: 'var(--color-tool-fx)' }} />
              <span className="text-[9px]">{k}: {String(v)}</span>
            </div>
          ))}
        </Section>
      )}
      {components.length > 0 && (
        <Section title="components">
          {components.map(([name, props]) => {
            // Component tokens are *agent-authored* and untyped at runtime: an LLM
            // often emits a Tailwind class string (e.g. "rounded-lg border p-4")
            // instead of a CSSProperties object. Passing a string to `style=`
            // crashes production React (it enumerates the string's char indices
            // and calls node.style["0"]=…, an unsupported indexed setter). Only
            // spread real objects; render anything else as its text.
            const styleObj =
              props && typeof props === 'object' && !Array.isArray(props)
                ? (props as React.CSSProperties)
                : undefined;
            return (
              <div key={name} className="mb-1">
                <span className="text-[9px]" style={{ color: 'var(--color-text-muted)' }}>{name}</span>
                <div style={styleObj}>{styleObj ? name : String(props)}</div>
              </div>
            );
          })}
        </Section>
      )}
    </div>
  );
}
